import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

/**
 * The "switch solution area" chip row — an orthogonal product-line filter
 * (the managed pillars + an Uncategorized bucket) layered on top of the
 * release-scoped doc list. The active selection lives in the URL
 * (`?solution=`); the page owns that state and passes the resolved
 * `options` / `counts` / `active` down here. When `manageHref` is set (editors
 * only) a trailing "Manage" link points at the solution-area admin page — the
 * discoverable place to add/rename/hide/delete pillars right where they show.
 */
export function SolutionSwitcher({
  options,
  counts,
  active,
  onSelect,
  manageHref,
}: {
  options: { value: string; label: string }[];
  counts: Record<string, number>;
  active: string;
  onSelect: (value: string) => void;
  manageHref?: string;
}): JSX.Element {
  return (
    <Box
      role="group"
      aria-label="Switch solution area"
      data-testid="solution-switcher"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        borderTop: 1,
        borderBottom: 1,
        borderColor: "divider",
        py: 1.25,
      }}
    >
      <Typography sx={{ mr: 0.5, fontSize: 12, fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Solution
      </Typography>
      {options.map((opt) => {
        const isActive = opt.value === active;
        return (
          <Chip
            key={opt.value}
            label={`${opt.label} (${counts[opt.value] ?? 0})`}
            onClick={() => onSelect(opt.value)}
            color={isActive ? "primary" : "default"}
            variant={isActive ? "filled" : "outlined"}
            aria-pressed={isActive}
            data-testid={`solution-chip-${opt.value}`}
            sx={{ borderRadius: 1.5, fontWeight: isActive ? 600 : 500, cursor: "pointer" }}
          />
        );
      })}
      {manageHref && (
        <MuiLink
          component={RouterLink}
          to={manageHref}
          underline="hover"
          data-testid="solution-manage-link"
          sx={{ ml: 0.5, fontSize: 12, fontWeight: 600, color: "text.secondary", "&:hover": { color: "primary.main" } }}
        >
          Manage
        </MuiLink>
      )}
    </Box>
  );
}
